import Vue from "vue";
import VueRouter from "vue-router";

const Login = () => import("../views/Login.vue");
const Register = () => import("../views/Register.vue");
const RecoverPassword = () => import("../views/RecoverPassword.vue");
const UpdatePassword = () => import("../views/UpdatePassword.vue");
const ChangePasswordView = () => import("../views/ChangePasswordView.vue");
const Profile = () => import("../views/Profile");
const ScheduleScreen = () => import("../views/ScheduleScreen");
const DoctorDetails = () => import("../views/DoctorDetails");
const Advantages = () => import("../views/Advantages");
const HowToWork = () => import("../views/HowToWork");
const FAQ = () => import("../views/FAQ");
const Dashboard = () => import("../views/Dashboard");
const Room = () => import("../views/Room");
const MeetingRoom = () => import("../views/MeetingRoom");
const PasswordMessageScreen = () => import("../views/PasswordMessageScreen");
const Confirmation = () => import("../views/Confirmation.vue");
const ScheduleSettings = () =>
  import("../views/ScheduleSettings/ScheduleSettings.vue");
const CreateSchedule = () => import("../views/CreateSchedule.vue");
const ScheduleResume = () => import("../views/ScheduleResume.vue");
const UpdateOperator = () => import("../views/Operator/UpdateOperator.vue");
const CreateOperator = () => import("../views/Operator/CreateOperator.vue");
const OperatorLogin = () => import("../views/OperatorLogin.vue");
const NeighborhoodChart = () => import("../views/Charts/NeighborhoodChart.vue");
const CitiesChart = () => import("../views/Charts/CitiesChart.vue");
const BoardChart = () => import("../views/Charts/Board.vue");
const Map = () => import("../views/Map.vue");
const ChartsHome = () => import("../views/Charts/Main.vue");
const InstitutionalUsers = () => import("../views/InstitutionalUsers");
const Alerts = () => import("../views/Alerts.vue");
const ConsultFinished = () => import("../views/ConsultFinished.vue");
const GuidePage = () => import("../views/GuidePage.vue");
const DoctorDocumentsPage = () => import("../views/DoctorDocumentsPage.vue");
const InstitutionAppointmentView = () =>
  import("../views/InstitutionAppointment.vue");
const AppointmentNotesHistory = () =>
  import("../views/AppointmentNotesHistory.vue");
const DoctorsList = () => import("../views/DoctorsList.vue");
const NewSlotSimpleForm = () => import("../views/NewSlotSimpleForm.vue");
const NurseSchedule = () => import("../views/NurseSchedule.vue");

const DoctorProfileView = () => import("../views/DoctorProfileView.vue");
const DoctorPatientsView = () => import("../views/DoctorPatientsView.vue");
const DoctorPatientDetailView = () =>
  import("../views/DoctorPatientDetailView.vue");
const DoctorClinicsView = () => import("../views/DoctorClinicsView.vue");

// Financeiro do Médico (views novas — Fase 6 do redesign)
const FinanceiroDashboard = () => import("../views/FinanceiroDashboard.vue");
const FinanceiroExtrato = () => import("../views/FinanceiroExtrato.vue");
const FinanceiroConsulta = () => import("../views/FinanceiroConsulta.vue");
const FinanceiroRepasse = () => import("../views/FinanceiroRepasse.vue");
const FinanceiroConta = () => import("../views/FinanceiroConta.vue");

const AdminLayout = () => import("../layouts/Admin.vue");
const DoctorAppLayout = () => import("../layouts/DoctorApp.vue");
const IntitutionLayout = () => import("../layouts/Intitution.vue");
const AuthLayout = () => import("../layouts/Auth.vue");
const SiteLayout = () => import("../layouts/Site.vue");
const RoomLayout = () => import("../layouts/Room.vue");

const BaseLayout = () => import("../layouts/Base.vue");

const HomeView = () => import("../views/HomeView.vue");
const PreLaunchView = () => import("../views/PreLaunchView.vue");
const LoginView = () => import("../views/LoginView.vue");
const RegisterView = () => import("../views/RegisterView.vue");
const InstitutionsView = () => import("../views/InstitutionsView.vue");
const InstitutionDetailsView = () =>
  import("../views/InstitutionDetailsView.vue");
const PartnerFormView = () => import("../views/PartnerFormView.vue");
const RoomView = () => import("../views/RoomView.vue");
const MeetingRoomView = () => import("../views/MeetingRoomView.vue");
const PrescriptionView = () => import("../views/PrescriptionView.vue");
const IndicationLinkView = () => import("../views/IndicationLinkView.vue");
const SchedulePaymentView = () => import("../views/SchedulePaymentView.vue");
const SchedulePaymentConfirmationView = () =>
  import("../views/SchedulePaymentConfirmationView.vue");
const ScheduleTimeView = () => import("../views/ScheduleTimeView.vue");
const ScheduleLoginView = () => import("../views/ScheduleLoginView.vue");
const WaitingRoomView = () => import("../views/WaitingRoomView.vue");

// Área interna do paciente (Fase 7 — área logada)
const ConsultasView = () => import("../views/ConsultasView.vue");
const AvaliarConsultaView = () => import("../views/AvaliarConsultaView.vue");
const DocumentosView = () => import("../views/DocumentosView.vue");
const PhoneVerificationView = () =>
  import("../views/PhoneVerificationView.vue");
const PatientPaymentsView = () => import("../views/PatientPaymentsView.vue");

import store from "../state/store";
import VueRouterMultiguard from "vue-router-multiguard";
import { isDoctor, isReceptionist } from "../utils/userRole";
import clinicaRoutes from "./clinica";
import { createPreLaunchGuard } from "./preLaunchGuard";

Vue.use(VueRouter);

const authMiddleware = (to, from, next) => {
  store.getters["auth/loggedIn"]
    ? next()
    : next({ name: "Login", query: { redirectFrom: to.fullPath } });
};

const isScheduling = (to, from, next) => {
  const schedule = store.state.appointments.currentSchedule;
  schedule.slot_id || schedule.doctor_id ? next() : next({ name: "Site" });
};

const isDoctorMiddleware = (to, from, next) => {
  isDoctor(store.state.user.currentUser) ? next() : next({ name: "Site" });
};

const isDoctorOrReceptionist = (to, from, next) => {
  const currentUser = store.state.user.currentUser;
  isDoctor(currentUser) || isReceptionist(currentUser)
    ? next()
    : next({ name: "Site" });
};

const userGuideMiddleware = (to, from, next) => {
  store.getters["auth/getterUserGuide"] ? next({ name: "GuidePage" }) : next();
};

const isPatientMiddleware = (to, from, next) => {
  isDoctor(store.state.user.currentUser) ? next({ name: "Dashboard" }) : next();
};

const routes = [
  {
    path: "/",
    name: "Home",
    component: HomeView,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([isPatientMiddleware, userGuideMiddleware])
  },
  {
    // Sem `meta.layout`: a experiência de pré-lançamento é uma barreira de tela
    // cheia, não uma página dentro do shell da aplicação.
    path: "/pre-lancamento",
    name: "PreLaunch",
    component: PreLaunchView
  },
  {
    path: "/login",
    name: "Login",
    component: LoginView,
    beforeEnter: VueRouterMultiguard([]),
    meta: { layout: BaseLayout }
  },
  {
    path: "/cadastrar",
    name: "Register",
    component: RegisterView,
    beforeEnter: VueRouterMultiguard([userGuideMiddleware]),
    meta: { layout: BaseLayout }
  },
  {
    path: "/verificar-telefone",
    name: "PhoneVerification",
    component: PhoneVerificationView,
    beforeEnter: VueRouterMultiguard([authMiddleware]),
    meta: { layout: BaseLayout }
  },
  {
    path: "/clinicas",
    name: "InstitutionsView",
    component: InstitutionsView,
    meta: { layout: BaseLayout }
  },
  {
    path: "/clinicas/:id/agendamento",
    name: "InstitutionDetailsView",
    component: InstitutionDetailsView,
    meta: { layout: BaseLayout }
  },
  {
    path: "/confirmacao",
    name: "Confirmation",
    component: SchedulePaymentConfirmationView,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([
      authMiddleware,
      isPatientMiddleware,
      userGuideMiddleware
    ])
  },
  {
    path: "/FAQ",
    name: "FAQ",
    component: FAQ,
    meta: { layout: AuthLayout },
    beforeEnter: VueRouterMultiguard([userGuideMiddleware])
  },
  {
    path: "/comofunciona",
    name: "How to work",
    component: HowToWork,
    meta: { layout: AuthLayout },
    beforeEnter: VueRouterMultiguard([userGuideMiddleware])
  },
  {
    path: "/updatePasswordMessage",
    name: "PasswordMessage",
    component: PasswordMessageScreen,
    meta: { layout: AuthLayout },
    beforeEnter: VueRouterMultiguard([userGuideMiddleware])
  },
  {
    path: "/vantagens",
    name: "Advantages",
    component: Advantages,
    meta: { layout: AuthLayout },
    beforeEnter: VueRouterMultiguard([userGuideMiddleware])
  },
  {
    path: "/agendamento/:doctor/horario",
    name: "SchedulePayment",
    component: ScheduleTimeView,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([userGuideMiddleware])
  },
  {
    path: "/agendamento/entrar",
    name: "ScheduleLogin",
    component: ScheduleLoginView,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([userGuideMiddleware])
  },
  {
    path: "/agendamento",
    name: "Schedule",
    component: SchedulePaymentView,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([isScheduling, userGuideMiddleware])
  },
  {
    path: "/recuperarSenha",
    name: "RecoverPassword",
    component: RecoverPassword,
    meta: { layout: AuthLayout },
    beforeEnter: VueRouterMultiguard([userGuideMiddleware])
  },
  {
    // US-C12: troca obrigatória da senha provisória da conta de equipe.
    path: "/trocar-senha",
    name: "ChangePassword",
    component: ChangePasswordView,
    beforeEnter: VueRouterMultiguard([authMiddleware]),
    meta: { layout: BaseLayout }
  },
  {
    path: "/atualizarSenha",
    name: "UpdatePassword",
    component: UpdatePassword,
    meta: { layout: AuthLayout },
    beforeEnter: VueRouterMultiguard([userGuideMiddleware])
  },
  {
    path: "/doutor/:doctor/detalhes",
    name: "DoctorDetails",
    component: DoctorDetails,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([userGuideMiddleware])
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([
      authMiddleware,
      isPatientMiddleware,
      userGuideMiddleware
    ])
  },
  {
    // Avaliação da consulta (Edital INAS, Anexo V, item 3.8 — etapa 15 da jornada)
    path: "/consultas/:id/avaliar",
    name: "AvaliarConsulta",
    component: AvaliarConsultaView,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([
      authMiddleware,
      isPatientMiddleware,
      userGuideMiddleware
    ])
  },
  {
    path: "/consultas",
    name: "Consultas",
    component: ConsultasView,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([
      authMiddleware,
      isPatientMiddleware,
      userGuideMiddleware
    ])
  },
  {
    path: "/documentos",
    name: "Documentos",
    component: DocumentosView,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([
      authMiddleware,
      isPatientMiddleware,
      userGuideMiddleware
    ])
  },
  {
    path: "/meus-pagamentos",
    name: "PatientPayments",
    component: PatientPaymentsView,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([
      authMiddleware,
      isPatientMiddleware,
      userGuideMiddleware
    ])
  },
  {
    path: "/medico/perfil",
    name: "DoctorProfile",
    component: DoctorProfileView,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorOrReceptionist])
  },
  {
    path: "/medico/pacientes",
    name: "DoctorPatients",
    component: DoctorPatientsView,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorMiddleware])
  },
  {
    // Convites de clínica e clínicas onde atende. Sem userGuideMiddleware: o
    // médico em onboarding também precisa ver e aceitar o convite.
    path: "/medico/clinicas",
    name: "DoctorClinics",
    component: DoctorClinicsView,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorMiddleware])
  },
  {
    path: "/medico/paciente/:appointmentId",
    name: "DoctorPatientDetail",
    component: DoctorPatientDetailView,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorMiddleware])
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: Dashboard,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([
      authMiddleware,
      isDoctorOrReceptionist,
      userGuideMiddleware
    ])
  },
  {
    path: "/consultorio/:room",
    name: "Room",
    component: Room,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/consultoriodaily/:room",
    name: "RoomView",
    component: RoomView,
    meta: { layout: RoomLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/consultorio/sala/:room",
    name: "MeetingRoom",
    component: MeetingRoom,
    meta: { layout: RoomLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/sala-de-espera/:room",
    name: "WaitingRoom",
    component: WaitingRoomView,
    meta: { layout: RoomLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/consultoriodaily/sala/:room",
    name: "MeetingRoomView",
    component: MeetingRoomView,
    meta: { layout: RoomLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/consultoriodaily/sala/:room/receita",
    name: "PrescriptionView",
    component: PrescriptionView,
    meta: { layout: RoomLayout },
    beforeEnter: VueRouterMultiguard([
      authMiddleware,
      isDoctorMiddleware,
      userGuideMiddleware
    ])
  },
  {
    path: "/configurar-agenda",
    name: "ScheduleSettings",
    component: ScheduleSettings,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorOrReceptionist])
  },
  {
    path: "/criar-agenda",
    name: "CreateSchedule",
    component: CreateSchedule,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorOrReceptionist])
  },
  {
    path: "/resumo-da-agenda",
    name: "ScheduleResume",
    component: ScheduleResume,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorOrReceptionist])
  },
  {
    path: "/login-operador",
    name: "OperatorLogin",
    component: OperatorLogin,
    meta: { layout: AuthLayout },
    beforeEnter: VueRouterMultiguard([userGuideMiddleware])
  },
  {
    path: "/cadastro-operador",
    name: "OperatorForm",
    component: CreateOperator,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/graficos/bairros",
    name: "Neighborhoods",
    component: NeighborhoodChart,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/graficos/detalhes-por-idade",
    name: "Board",
    component: BoardChart,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/mapa",
    name: "Map",
    component: Map,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/graficos/cidades",
    name: "CitiesChart",
    component: CitiesChart,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/graficos",
    name: "ChartHome",
    component: ChartsHome,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/perfil/operador",
    name: "UpdateOperator",
    component: UpdateOperator,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/institucional/usuarios",
    name: "InstitutionalUsers",
    component: InstitutionalUsers,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/alertas",
    name: "Alerts",
    component: Alerts,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, userGuideMiddleware])
  },
  {
    path: "/consultorio/sala/finalizar/:room",
    name: "ConsultFinished",
    component: ConsultFinished,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorOrReceptionist])
  },
  {
    path: "/guide",
    name: "GuidePage",
    component: GuidePage,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorMiddleware])
  },
  {
    path: "/guide/documentos",
    name: "DoctorDocumentsPage",
    component: DoctorDocumentsPage,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorMiddleware])
  },
  {
    path: "/agendamentos/:id/anotacoes",
    name: "AppointmentNotesHistory",
    component: AppointmentNotesHistory,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorOrReceptionist])
  },
  {
    path: "/profissionais",
    name: "DoctorsList",
    component: DoctorsList,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorOrReceptionist])
  },
  {
    path: "/newslotsimpleform",
    name: "NewSlotSimpleForm",
    component: NewSlotSimpleForm,
    meta: { layout: AdminLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorOrReceptionist])
  },
  {
    path: "/nurseschedule",
    name: "NurseSchedule",
    component: NurseSchedule,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorOrReceptionist])
  },
  {
    path: "/cadastro-parceiro",
    name: "PartnerForm",
    component: PartnerFormView,
    meta: { layout: BaseLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware])
  },
  {
    path: "/indicar-para-amigos",
    name: "IndicationLink",
    component: IndicationLinkView,
    meta: { layout: BaseLayout }
  },

  // ── Financeiro do Médico (Fase 6 do redesign) ──────────────────────────
  {
    path: "/financeiro",
    name: "FinanceiroDashboard",
    component: FinanceiroDashboard,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorMiddleware])
  },
  {
    path: "/financeiro/extrato",
    name: "FinanceiroExtrato",
    component: FinanceiroExtrato,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorMiddleware])
  },
  {
    path: "/financeiro/consultas/:id",
    name: "FinanceiroConsulta",
    component: FinanceiroConsulta,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorMiddleware])
  },
  {
    path: "/financeiro/repasses/:id",
    name: "FinanceiroRepasse",
    component: FinanceiroRepasse,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorMiddleware])
  },
  {
    path: "/financeiro/conta-bancaria",
    name: "FinanceiroConta",
    component: FinanceiroConta,
    meta: { layout: DoctorAppLayout },
    beforeEnter: VueRouterMultiguard([authMiddleware, isDoctorMiddleware])
  },

  // ── Módulo Clínica (/clinica) — terceiro módulo, aditivo ───────────────
  ...clinicaRoutes
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes
});

// Guard único do Modo Pré-Lançamento — roda antes de qualquer beforeEnter.
router.beforeEach(createPreLaunchGuard(() => store));

export default router;
